'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import Button from '@/components/button/Button';

import { CinemaRoomDto } from '@cineamo/api/models/cinema-room/CinemaRoomDto.types';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';

const CinemaRoomPlaceholderImage = loadAsset('/images/placeholders/im-cinemaScreenPlaceholder.png');

type CinemaRoomSliderItemProps = {
    domainConfiguration: DomainConfigurationDto;
    cinemaRoom: CinemaRoomDto;
    onClick?: (cinemaRoom: CinemaRoomDto) => void;
};

export default function CinemaRoomSliderItem(props: CinemaRoomSliderItemProps): React.JSX.Element {
    const { domainConfiguration, cinemaRoom, onClick } = props;

    const t = useTranslations();

    const name = cinemaRoom.name;
    const seatCount = cinemaRoom.seatCount;
    const imageSrc = cinemaRoom.profileImageUrl;

    return (
        <div
            className="w-[356px] lg:w-full md:h-auto flex flex-col space-y-10 group min-h-full"
            style={{
                color: domainConfiguration.style.onBackgroundColor
            }}>
            <div className="rounded-8 overflow-hidden flex-none">
                <ImageWithTransition
                    className="w-full h-auto aspect-[3/2] object-cover group-hover:scale-105 duration-200 overflow-hidden"
                    src={imageSrc || CinemaRoomPlaceholderImage}
                    width={800}
                    height={600}
                    alt={t('info.cinema-room-image-alt')}
                />
            </div>
            <div className="flex flex-row space-x-16 flex-auto">
                <div className="w-full md:w-200 flex flex-col flex-none">
                    <span
                        className="text-body-bold-16 leading-[24px]"
                        style={
                            domainConfiguration
                                ? {
                                      color: domainConfiguration.style.onBackgroundColor
                                  }
                                : undefined
                        }>
                        {name}
                    </span>
                    <DotSeparatedLabel
                        className="text-body-14 opacity-70"
                        labels={[seatCount ? `${seatCount.toString()} ${t('cinema.seats')}` : undefined]}
                    />
                </div>
            </div>
            <div className="w-full">
                <Button
                    className="w-full h-36"
                    ariaLabel={t('info.show-all-features-room', { roomName: cinemaRoom.name })}
                    labelClassName="text-body-bold-14"
                    css={
                        domainConfiguration
                            ? {
                                  backgroundColor: domainConfiguration.style.surfaceColor,
                                  color: domainConfiguration.style.onSurfaceColor
                              }
                            : undefined
                    }
                    onClick={() => onClick && onClick(cinemaRoom)}>
                    {t('info.show-all-features')}
                </Button>
            </div>
        </div>
    );
}
